import { LoginRequest, LoginResponse } from '../types/authService';
import { apiFetch } from './apiClient';
import { API_URL } from '@/lib/utils';
import { trackedFetch } from './serverStatus';


export const userLogin = async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
        const response = await trackedFetch(`${API_URL}/user/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(credentials),
        });

        console.log('Login response status:', response.status);
        const responseData = await response.json();
        console.log('Login response data:', responseData);

        if (!response.ok) {
            throw new Error(responseData.detail || 'Login failed');
        }

        const authData: LoginResponse = responseData;

        localStorage.setItem('access_token', authData.access_token);
        localStorage.setItem('refresh_token', authData.refresh_token);
        localStorage.setItem('user', JSON.stringify(authData.user));
        return authData;
    } catch (error) {
        console.error('Login error:', error);
        throw error;
    }
};

export const startSSOLogin = () => {
    window.location.href = `${API_URL}/auth/sso/login?_=${Date.now()}`;
};

const decodeJwt = (token: string): Record<string, unknown> => {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
};

export const completeSSOLogin = (): LoginResponse['user'] => {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const error = params.get('error');
    if (error) {
        throw new Error(error);
    }
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    if (!access_token || !refresh_token) {
        throw new Error('sso_no_tokens');
    }

    const claims = decodeJwt(access_token);
    const user = {
        id: String(claims.sub ?? ''),
        role: String(claims.role ?? 'user'),
        email: String(claims.email ?? ''),
        name: String(claims.name ?? ''),
        must_change_password: false,
        is_active: false,
    };

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(user));
    return user;
};

export const logout = () => {
    apiFetch(`${API_URL}/user/logout`, { method: 'POST' }).catch(() => {
    });
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
};

export const isAuthenticated = (): boolean => {
    return !!localStorage.getItem("access_token")
}

export const getStoredUser = () => {
    const user = localStorage.getItem("user")
    return user ? JSON.parse(user) : null;
}

export const getAccessToken = () => {
    return localStorage.getItem('access_token');
};

export const getUser = () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
};

export const markActive = async (): Promise<void> => {
    const user = getUser();
    if (user?.is_active) return;
    try {
        const res = await apiFetch(`${API_URL}/user/activate`, { method: 'POST' });
        if (!res.ok) return;
        if (user) {
            localStorage.setItem('user', JSON.stringify({ ...user, is_active: true }));
        }
    } catch {
    }
};

export const updateName = async (name: string): Promise<string> => {
    const res = await apiFetch(`${API_URL}/user/me/name`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
    });
    if (!res.ok) {
        let detail = 'Failed to update name';
        try {
            const body = await res.json();
            if (typeof body?.detail === 'string') detail = body.detail;
            else if (Array.isArray(body?.detail) && body.detail[0]?.msg) detail = body.detail[0].msg;
        } catch {
            /* ignore */
        }
        throw new Error(detail);
    }
    const { name: updated } = await res.json();
    const user = getUser();
    if (user) {
        localStorage.setItem('user', JSON.stringify({ ...user, name: updated }));
    }
    return updated;
};

export const changePassword = async (newPassword: string): Promise<void> => {
    const res = await apiFetch(`${API_URL}/user/change-password`, {
        method: 'POST',
        body: JSON.stringify({ new_password: newPassword }),
    });
    if (!res.ok) {
        let detail = 'Failed to update password';
        try {
            const body = await res.json();
            if (typeof body?.detail === 'string') detail = body.detail;
            else if (Array.isArray(body?.detail) && body.detail[0]?.msg) detail = body.detail[0].msg;
        } catch {
            /* ignore */
        }
        throw new Error(detail);
    }
    const user = getUser();
    if (user) {
        localStorage.setItem('user', JSON.stringify({ ...user, must_change_password: false }));
    }
};